import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import M from "materialize-css";
import { icon } from "../../components/assets/assets";
import NextButton from "../../components/buttons/next-button";
import NormalButton from "../../components/buttons/normal-button";
import AmpleHireImageTag from "../../components/ample-hire-html-tags/ample-hire-image-tag";
import {
  getCity,
  getCountries,
  getState,
} from "../../redux/actions/location-actions/location-actions";
import { saveEntity } from "../../redux/actions/sign-up-actions";
import {
  validateEmailInput,
  validateNames,
  validateNumbers,
  validateLinkedinUrlInput,
} from "../../utils/validationRegex";
import WarningToast from "../../components/toasts/warning-toast";
import { optionMapper } from "../../utils/optionMapper";
import { isValidURL, checkPasswordStrength } from "../../utils/validationRegex";
import { getCountryCode, getPhoneNumber } from "../../utils/phoneNumberUtils";
import { setEntity } from "../../redux/slices/sign-up-slice";
import RegistrationVerificationModal from "./registration-verification-modal";

// --- Inline style tokens (same as login page) ---
const S = {
  row: { display: "flex", gap: "14px", marginBottom: "2px" },
  cell: { flex: 1, minWidth: 0 },
  label: {
    display: "block",
    fontSize: "11px",
    fontWeight: 600,
    color: "#555770",
    marginBottom: "4px",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  input: {
    width: "100%",
    height: "40px",
    padding: "0 12px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#1a1a2e",
    background: "#f7f8fc",
    border: "1.5px solid #e2e4ed",
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
    transition: "border-color 0.25s, box-shadow 0.25s",
  },
  select: {
    width: "100%",
    height: "40px",
    padding: "0 10px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#1a1a2e",
    background: "#f7f8fc",
    border: "1.5px solid #e2e4ed",
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Inter', sans-serif",
    cursor: "pointer",
    WebkitAppearance: "auto",
  },
  fieldWrap: { marginBottom: "6px" },
};

const onFocus = (e) => {
  e.target.style.borderColor = "#E8003A";
  e.target.style.boxShadow = "0 0 0 3px rgba(8,106,216,0.1)";
  e.target.style.background = "#fff";
};
const onBlurStyle = (e) => {
  e.target.style.borderColor = "#e2e4ed";
  e.target.style.boxShadow = "none";
  e.target.style.background = "#f7f8fc";
};

// Simple field components
const Field = ({ label, required, children }) => (
  <div style={S.fieldWrap}>
    <label style={S.label}>
      {label}
      {required && <span style={{ color: "#f9103b" }}> *</span>}
    </label>
    {children}
  </div>
);

const Input = ({ label, required, ...props }) => (
  <Field label={label} required={required}>
    <input
      style={S.input}
      onFocus={onFocus}
      onBlur={(e) => {
        onBlurStyle(e);
        props.onBlur?.(e);
      }}
      {...props}
    />
  </Field>
);

const Select = ({ label, required, options = [], value, onChange, name }) => (
  <Field label={label} required={required}>
    <select
      style={S.select}
      value={value || ""}
      onChange={onChange}
      name={name}
      onFocus={onFocus}
      onBlur={onBlurStyle}
    >
      <option value="" disabled>
        Select {label}
      </option>
      {options?.map(
        (o) =>
          o?.optionKey && (
            <option key={o.optionValue} value={o.optionValue}>
              {o.optionKey}
            </option>
          ),
      )}
    </select>
  </Field>
);

// --- Country list for the vertical phone input ---
const COUNTRY_LIST = [
  {
    flag: "fi fi-in",
    code: "IN",
    dialCode: "+91",
    name: "India",
    phoneLength: 10,
  },
  {
    flag: "fi fi-us",
    code: "US",
    dialCode: "+1",
    name: "United States",
    phoneLength: 10,
  },
  {
    flag: "fi fi-gb",
    code: "GB",
    dialCode: "+44",
    name: "United Kingdom",
    phoneLength: 10,
  },
  {
    flag: "fi fi-ca",
    code: "CA",
    dialCode: "+1",
    name: "Canada",
    phoneLength: 10,
  },
  {
    flag: "fi fi-au",
    code: "AU",
    dialCode: "+61",
    name: "Australia",
    phoneLength: 9,
  },
  {
    flag: "fi fi-ae",
    code: "AE",
    dialCode: "+971",
    name: "United Arab Emirates",
    phoneLength: 9,
  },
  {
    flag: "fi fi-sa",
    code: "SA",
    dialCode: "+966",
    name: "Saudi Arabia",
    phoneLength: 9,
  },
  {
    flag: "fi fi-sg",
    code: "SG",
    dialCode: "+65",
    name: "Singapore",
    phoneLength: 8,
  },
  {
    flag: "fi fi-de",
    code: "DE",
    dialCode: "+49",
    name: "Germany",
    phoneLength: 11,
  },
  {
    flag: "fi fi-fr",
    code: "FR",
    dialCode: "+33",
    name: "France",
    phoneLength: 9,
  },
  {
    flag: "fi fi-it",
    code: "IT",
    dialCode: "+39",
    name: "Italy",
    phoneLength: 10,
  },
  {
    flag: "fi fi-es",
    code: "ES",
    dialCode: "+34",
    name: "Spain",
    phoneLength: 9,
  },
  {
    flag: "fi fi-nl",
    code: "NL",
    dialCode: "+31",
    name: "Netherlands",
    phoneLength: 10,
  },
  {
    flag: "fi fi-ch",
    code: "CH",
    dialCode: "+41",
    name: "Switzerland",
    phoneLength: 9,
  },
  {
    flag: "fi fi-se",
    code: "SE",
    dialCode: "+46",
    name: "Sweden",
    phoneLength: 10,
  },
  {
    flag: "fi fi-pl",
    code: "PL",
    dialCode: "+48",
    name: "Poland",
    phoneLength: 9,
  },
  {
    flag: "fi fi-tr",
    code: "TR",
    dialCode: "+90",
    name: "Turkey",
    phoneLength: 10,
  },
  {
    flag: "fi fi-jp",
    code: "JP",
    dialCode: "+81",
    name: "Japan",
    phoneLength: 11,
  },
  {
    flag: "fi fi-kr",
    code: "KR",
    dialCode: "+82",
    name: "South Korea",
    phoneLength: 11,
  },
  {
    flag: "fi fi-cn",
    code: "CN",
    dialCode: "+86",
    name: "China",
    phoneLength: 11,
  },
  {
    flag: "fi fi-hk",
    code: "HK",
    dialCode: "+852",
    name: "Hong Kong",
    phoneLength: 8,
  },
  {
    flag: "fi fi-tw",
    code: "TW",
    dialCode: "+886",
    name: "Taiwan",
    phoneLength: 10,
  },
  {
    flag: "fi fi-my",
    code: "MY",
    dialCode: "+60",
    name: "Malaysia",
    phoneLength: 10,
  },
  {
    flag: "fi fi-id",
    code: "ID",
    dialCode: "+62",
    name: "Indonesia",
    phoneLength: 10,
  },
  {
    flag: "fi fi-ph",
    code: "PH",
    dialCode: "+63",
    name: "Philippines",
    phoneLength: 10,
  },
  {
    flag: "fi fi-th",
    code: "TH",
    dialCode: "+66",
    name: "Thailand",
    phoneLength: 9,
  },
  {
    flag: "fi fi-vn",
    code: "VN",
    dialCode: "+84",
    name: "Vietnam",
    phoneLength: 10,
  },
  {
    flag: "fi fi-pk",
    code: "PK",
    dialCode: "+92",
    name: "Pakistan",
    phoneLength: 10,
  },
  {
    flag: "fi fi-bd",
    code: "BD",
    dialCode: "+880",
    name: "Bangladesh",
    phoneLength: 10,
  },
  {
    flag: "fi fi-za",
    code: "ZA",
    dialCode: "+27",
    name: "South Africa",
    phoneLength: 10,
  },
  {
    flag: "fi fi-eg",
    code: "EG",
    dialCode: "+20",
    name: "Egypt",
    phoneLength: 10,
  },
  {
    flag: "fi fi-ke",
    code: "KE",
    dialCode: "+254",
    name: "Kenya",
    phoneLength: 10,
  },
  {
    flag: "fi fi-br",
    code: "BR",
    dialCode: "+55",
    name: "Brazil",
    phoneLength: 11,
  },
  {
    flag: "fi fi-mx",
    code: "MX",
    dialCode: "+52",
    name: "Mexico",
    phoneLength: 10,
  },
  {
    flag: "fi fi-ru",
    code: "RU",
    dialCode: "+7",
    name: "Russia",
    phoneLength: 10,
  },
  {
    flag: "fi fi-nz",
    code: "NZ",
    dialCode: "+64",
    name: "New Zealand",
    phoneLength: 10,
  },
];

const DEFAULT_PHONE_COUNTRY = COUNTRY_LIST[0];

const phoneStyles = {
  wrapper: {
    display: "flex",
    alignItems: "stretch",
    gap: "8px",
    position: "relative",
    width: "100%",
  },
  selectorBox: {
    position: "relative",
    flex: "0 0 auto",
  },
  selector: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "3px",
    height: "40px",
    padding: "0 6px",
    background: "#f7f8fc",
    border: "1.5px solid #e2e4ed",
    borderRadius: "8px",
    cursor: "pointer",
    outline: "none",
    fontFamily: "'Inter', sans-serif",
    fontSize: "13px",
    fontWeight: 600,
    color: "#1a1a2e",
    lineHeight: 1,
    boxSizing: "border-box",
  },
  arrow: { fontSize: "9px", color: "#8b8da3" },
  numberInput: {
    flex: "1 1 auto",
    width: "100%",
    minWidth: 0,
    height: "40px",
    padding: "0 12px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#1a1a2e",
    background: "#f7f8fc",
    border: "1.5px solid #e2e4ed",
    borderRadius: "8px",
    outline: "none",
    fontFamily: "'Inter', sans-serif",
    boxSizing: "border-box",
    transition: "border-color 0.25s, box-shadow 0.25s",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    width: "240px",
    maxHeight: "240px",
    overflowY: "auto",
    background: "#fff",
    border: "1px solid #e2e4ed",
    borderRadius: "8px",
    boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
    zIndex: 1000,
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 10px",
    fontSize: "13px",
    cursor: "pointer",
    color: "#1a1a2e",
  },
};

const PhoneInput = ({
  countryCode,
  nationalNumber,
  onChange,
  placeholder = "Mobile Number",
  name,
}) => {
  const initial =
    COUNTRY_LIST.find((c) => c.dialCode === countryCode) ||
    DEFAULT_PHONE_COUNTRY;
  const [selected, setSelected] = useState(initial);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (countryCode && countryCode !== selected.dialCode) {
      const found = COUNTRY_LIST.find((c) => c.dialCode === countryCode);
      if (found) setSelected(found);
    }
  }, [countryCode]);

  const handleSelect = (country) => {
    setSelected(country);
    setOpen(false);
    onChange({
      countryCode: country.dialCode,
      nationalNumber: nationalNumber || "",
    });
  };

  const handleNumberChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw.length <= selected.phoneLength) {
      onChange({ countryCode: selected.dialCode, nationalNumber: raw });
    }
  };

  return (
    <div style={phoneStyles.wrapper}>
      <div style={phoneStyles.selectorBox}>
        <button
          type="button"
          style={phoneStyles.selector}
          onClick={() => setOpen((o) => !o)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        >
          <span>{selected.dialCode}</span>
          <span style={phoneStyles.arrow}>▼</span>
        </button>
        {open && (
          <div style={phoneStyles.dropdown}>
            {COUNTRY_LIST.map((c) => (
              <div
                key={`${c.code}_${c.dialCode}`}
                style={phoneStyles.option}
                onMouseDown={() => handleSelect(c)}
              >
                <span className={c.flag} />
                <span style={{ flex: 1 }}>{c.name}</span>
                <span style={{ color: "#8b8da3", fontWeight: 600 }}>
                  {c.dialCode}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <input
        type="tel"
        name={name}
        value={nationalNumber || ""}
        onChange={handleNumberChange}
        onFocus={onFocus}
        onBlur={onBlurStyle}
        placeholder={placeholder}
        style={phoneStyles.numberInput}
      />
    </div>
  );
};

const PasswordField = ({ label, required, ...props }) => {
  const [show, setShow] = useState(false);
  return (
    <Field label={label} required={required}>
      <div style={{ position: "relative" }}>
        <input
          style={S.input}
          type={show ? "text" : "password"}
          onFocus={onFocus}
          onBlur={(e) => {
            onBlurStyle(e);
            props.onBlur?.(e);
          }}
          {...props}
        />
        <span
          onClick={() => setShow(!show)}
          style={{
            position: "absolute",
            right: 10,
            top: 10,
            cursor: "pointer",
            fontSize: 13,
            color: "#8b8da3",
            userSelect: "none",
          }}
        >
          {show ? "Hide" : "Show"}
        </span>
      </div>
    </Field>
  );
};

const EntitySignupStep1 = ({
  newEntity,
  setNewEntity,
  handleOnChangeEntityData,
  checkUniqueBusinessName,
  checkUniqueWebsiteUrl,
  checkValidPinCode,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState({});
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleWebsiteChange = (e) =>
    setNewEntity({ ...newEntity, webSite: e.target.value });

  const listOfEntity = [
    { optionValue: "Employer/Corporate", optionKey: "Employer/Corporate" },
    { optionValue: "Placement Agency", optionKey: "Placement Agency" },
    { optionValue: "College/University", optionKey: "College/University" },
    { optionValue: "Training Institute", optionKey: "Training Institute" },
    { optionValue: "Others", optionKey: "Others" },
  ];

  const { countries, states, cities } = useSelector(
    (state) => state.manageLocationsSliceReducer,
  );

  useEffect(() => {
    document.title = "New Entity Registration";
    if (localStorage.getItem("organizationDTO")) {
      setNewEntity(JSON.parse(localStorage.getItem("organizationDTO")));
    }
  }, []);

  useEffect(() => {
    dispatch(getCountries());
  }, []);
  useEffect(() => {
    dispatch(getState({ country_id: newEntity.country || "1" }));
  }, [newEntity.country]);
  useEffect(() => {
    if (newEntity.hqstate) dispatch(getCity(newEntity.hqstate));
  }, [newEntity.hqstate]);

  const handleAdd = () => {
    setNewEntity((prev) => ({
      ...prev,
      contactPersonDetails: [
        ...prev.contactPersonDetails,
        {
          firstName: "",
          lastName: "",
          whatsappNumber: "",
          whatsappNumberCountryCode: "",
          whatsappNumberVerified: false,
          email: "",
          designation: "",
          linkdinUrl: "",
          mobileNumberCountryCode: "",
          mobileNumberVerified: false,
          mobileNumber: "",
          password: "",
          confirmPassword: "",
        },
      ],
    }));
  };

  const handleRemove = (index) => {
    const updated = [...newEntity.contactPersonDetails];
    updated.splice(index, 1);
    setNewEntity({ ...newEntity, contactPersonDetails: updated });
  };

  const handleFormChange = (e, index) => {
    const { name, value } = e.target;
    const updated = [...newEntity.contactPersonDetails];
    updated[index] = { ...updated[index], [name]: value };
    setNewEntity({ ...newEntity, contactPersonDetails: updated });
  };

  const handleOnClickNextButton = () => {
    const { businessName, webSite } = newEntity;
    const isWebsiteEmpty = webSite === "https://";

    if (
      !businessName ||
      isWebsiteEmpty ||
      newEntity.contactPersonDetails.some(
        (c) =>
          !c.firstName ||
          !c.lastName ||
          !c.whatsappNumber ||
          !c.email ||
          !c.mobileNumber ||
          !c.password ||
          !c.confirmPassword,
      )
    ) {
      return WarningToast("Please fill all required fields");
    }

    const passwordsMatch = newEntity.contactPersonDetails.every((c) => {
      const err = checkPasswordStrength(c.password);
      return !err && c.password === c.confirmPassword;
    });
    if (!passwordsMatch)
      return WarningToast("Passwords do not match or are weak.");

    setVerificationModalOpen(true);
  };

  const proceedToSave = async () => {
    setVerificationModalOpen(false);
    setIsSaving(true);
    try {
      const res = await dispatch(
        saveEntity({
          ...newEntity,
          id: localStorage?.getItem("entityId"),
          billingAddress:
            JSON.parse(localStorage.getItem("organizationDTO"))
              ?.billingAddress || null,
        }),
      );

      if (
        res.type === "saveEntity/fulfilled" &&
        res?.payload?.message === "Request processed successfully!"
      ) {
        navigate("/entity-signup/step-2");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isCollege = newEntity.entityType === "College/University";

  return (
    <>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          paddingBottom: 14,
          borderBottom: "1px solid #eef0f5",
        }}
      >
        <h2
          style={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e", margin: 0 }}
        >
          Register Now
        </h2>
        <span
          style={{
            fontSize: 13,
            color: "#8b8da3",
            background: "#f0f2f8",
            padding: "4px 14px",
            borderRadius: 20,
          }}
        >
          Step <strong style={{ color: "#E8003A" }}>1</strong> of 3
        </span>
      </div>

      {/* Account Type */}
      <div style={{ maxWidth: 300, marginBottom: 14 }}>
        <Select
          label="Account Type"
          name="entityType"
          value={newEntity.entityType}
          onChange={handleOnChangeEntityData}
          options={optionMapper(
            listOfEntity,
            "optionKey",
            "optionValue",
            "Select account type",
          )}
        />
      </div>

      {/* Progress */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "14px 0 20px",
        }}
      >
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#E8003A",
            border: "2px solid #E8003A",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AmpleHireImageTag
            className="whiteColorFilter"
            imgSrc={icon.accountIcon}
            altText="1"
            style={{ width: 16 }}
          />
        </div>
        <div style={{ width: 48, height: 2, background: "#e2e4ed" }} />
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#f7f8fc",
            border: "2px solid #e2e4ed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AmpleHireImageTag
            imgSrc={icon.idCardIcon}
            altText="2"
            style={{ width: 16 }}
          />
        </div>
        <div style={{ width: 48, height: 2, background: "#e2e4ed" }} />
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: "#f7f8fc",
            border: "2px solid #e2e4ed",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AmpleHireImageTag
            imgSrc={icon.penToolIcon}
            altText="3"
            style={{ width: 16 }}
          />
        </div>
      </div>

      {newEntity.entityType !== "Job Seeker/Candidate" && (
        <>
          {/* Organization Details */}
          <h4
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#1a1a2e",
              margin: "16px 0 8px",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            Organization Details
          </h4>

          <div style={S.row}>
            <div style={S.cell}>
              <Input
                label={isCollege ? "Institution Name" : "Organization Name"}
                required
                name="businessName"
                value={newEntity.businessName}
                onChange={handleOnChangeEntityData}
                onBlur={checkUniqueBusinessName}
                placeholder={
                  isCollege ? "Institution Name" : "Organization Name"
                }
              />
            </div>
            <div style={S.cell}>
              <Input
                label={isCollege ? "Campus Short Name" : "Business Short Name"}
                name="businessShortName"
                value={newEntity.businessShortName}
                onChange={handleOnChangeEntityData}
                placeholder="Popularly known as"
              />
            </div>
            <div style={S.cell}>
              <Select
                label="Country"
                name="country"
                value={newEntity.country || "1"}
                options={countries}
                onChange={(e) => {
                  handleOnChangeEntityData(e);
                  dispatch(getState({ country_id: e.target.value }));
                }}
              />
            </div>
          </div>

          <div style={S.row}>
            <div style={S.cell}>
              <Select
                label={isCollege ? "Campus State" : "HQ State"}
                name="hqstate"
                value={newEntity.hqstate}
                options={states}
                onChange={(e) => {
                  handleOnChangeEntityData(e);
                  dispatch(getCity(e.target.value));
                }}
              />
            </div>
            <div style={S.cell}>
              <Select
                label={isCollege ? "Campus City" : "HQ City"}
                name="hqcity"
                value={newEntity.hqcity}
                options={cities}
                onChange={handleOnChangeEntityData}
              />
            </div>
            <div style={S.cell}>
              <Input
                label="PIN Code"
                name="pincode"
                type="number"
                value={newEntity.pincode}
                onChange={handleOnChangeEntityData}
                onBlur={checkValidPinCode}
                placeholder="PIN Code"
              />
            </div>
          </div>

          <div style={S.row}>
            <div style={S.cell}>
              <Select
                label={isCollege ? "No of Students" : "No Of Employees"}
                name="noOfEmployees"
                value={newEntity.noOfEmployees}
                onChange={handleOnChangeEntityData}
                options={[
                  "1-10",
                  "11-25",
                  "26-50",
                  "51-200",
                  "201-500",
                  "501-1000",
                  "1001-5000",
                  "5001-10000",
                  "10001-25000",
                  ">25000",
                ].map((v) => ({ optionKey: v, optionValue: v }))}
              />
            </div>
            <div style={S.cell}>
              <Input
                label="Company Website"
                required
                name="webSite"
                value={newEntity.webSite}
                onChange={handleWebsiteChange}
                placeholder="https://yourcompany.com"
                onBlur={() => {
                  if (newEntity.webSite && !isValidURL(newEntity.webSite))
                    return WarningToast("Invalid Url");
                  checkUniqueWebsiteUrl(newEntity.webSite);
                }}
              />
            </div>
            <div style={S.cell} />
          </div>

          {/* Contact Person Section */}
          <hr
            style={{
              border: "none",
              borderTop: "1px solid #eef0f5",
              margin: "20px 0 12px",
            }}
          />
          <h4
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#1a1a2e",
              margin: "0 0 10px",
              textTransform: "uppercase",
              letterSpacing: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <AmpleHireImageTag
              className="secondaryColorFilter"
              imgSrc={icon.accountIcon}
              altText=""
              style={{ width: 16 }}
            />
            Contact Person Details
          </h4>

          {newEntity?.contactPersonDetails?.map((inputs, index) => (
            <div
              key={`cp_${index}`}
              style={{
                background: "#f7f8fc",
                border: "1px solid #eef0f5",
                borderRadius: 12,
                padding: "14px 14px 6px",
                marginBottom: 10,
              }}
            >
              <div style={S.row}>
                <div style={S.cell}>
                  <Input
                    label="First Name"
                    required
                    name="firstName"
                    value={inputs.firstName}
                    onChange={(e) => handleFormChange(e, index)}
                    placeholder="First Name"
                  />
                </div>
                <div style={S.cell}>
                  <Input
                    label="Last Name"
                    required
                    name="lastName"
                    value={inputs.lastName}
                    onChange={(e) => handleFormChange(e, index)}
                    placeholder="Last Name"
                  />
                </div>
                <div style={S.cell}>
                  <Field label="WhatsApp No" required>
                    <PhoneInput
                      name="whatsappNumber"
                      placeholder="WhatsApp Number"
                      countryCode={inputs.whatsappNumberCountryCode || "+91"}
                      nationalNumber={inputs.whatsappNumber}
                      onChange={({ countryCode, nationalNumber }) => {
                        const updated = [...newEntity.contactPersonDetails];
                        updated[index] = {
                          ...updated[index],
                          whatsappNumberCountryCode: countryCode,
                          whatsappNumber: nationalNumber,
                        };
                        setNewEntity({
                          ...newEntity,
                          contactPersonDetails: updated,
                        });
                      }}
                    />
                  </Field>
                </div>
              </div>

              <div style={S.row}>
                <div style={S.cell}>
                  <Input
                    label="Email"
                    required
                    name="email"
                    type="email"
                    value={inputs.email}
                    onChange={(e) => handleFormChange(e, index)}
                    placeholder="Email Address"
                    onBlur={(e) => validateEmailInput(e, WarningToast)}
                  />
                </div>
                <div style={S.cell}>
                  <Input
                    label="Designation"
                    name="designation"
                    value={inputs.designation}
                    onChange={(e) => handleFormChange(e, index)}
                    placeholder="Designation"
                  />
                </div>
                <div style={S.cell}>
                  <Input
                    label="LinkedIn URL"
                    name="linkdinUrl"
                    value={inputs.linkdinUrl}
                    onChange={(e) => handleFormChange(e, index)}
                    placeholder="LinkedIn URL"
                    onBlur={(e) => validateLinkedinUrlInput(e, WarningToast)}
                  />
                </div>
              </div>

              <div style={S.row}>
                <div style={S.cell}>
                  <Field label="Mobile No" required>
                    <PhoneInput
                      name="mobileNumber"
                      placeholder="Mobile Number"
                      countryCode={inputs.mobileNumberCountryCode || "+91"}
                      nationalNumber={inputs.mobileNumber}
                      onChange={({ countryCode, nationalNumber }) => {
                        const updated = [...newEntity.contactPersonDetails];
                        updated[index] = {
                          ...updated[index],
                          mobileNumberCountryCode: countryCode,
                          mobileNumber: nationalNumber,
                        };
                        setNewEntity({
                          ...newEntity,
                          contactPersonDetails: updated,
                        });
                      }}
                    />
                  </Field>
                </div>
                <div style={S.cell}>
                  <PasswordField
                    label="Password"
                    required
                    name="password"
                    value={inputs.password}
                    onChange={(e) => handleFormChange(e, index)}
                    placeholder="Password"
                    onBlur={(e) => {
                      if (checkPasswordStrength(e.target.value) !== null)
                        return WarningToast("Password strength weak!");
                    }}
                  />
                </div>
                <div style={S.cell}>
                  <PasswordField
                    label="Confirm Password"
                    required
                    name="confirmPassword"
                    value={inputs.confirmPassword}
                    onChange={(e) => handleFormChange(e, index)}
                    placeholder="Confirm Password"
                    onBlur={(e) => {
                      if (e.target.value !== inputs.password)
                        return WarningToast("Password did not match!");
                    }}
                  />
                </div>
              </div>

              {newEntity.contactPersonDetails.length > 1 && (
                <div style={{ textAlign: "right", padding: "2px 0 6px" }}>
                  <NormalButton
                    buttonTagCssClasses="btn-clear btn-small"
                    buttonText="Remove"
                    onClick={() => handleRemove(index)}
                  />
                </div>
              )}
            </div>
          ))}

          {/* Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 12,
              marginTop: 20,
              paddingTop: 16,
              borderTop: "1px solid #eef0f5",
            }}
          >
            <NormalButton
              buttonTagCssClasses="btn-clear btn-small add-contact-person-btn"
              leftIconSrc={icon.mathPlusIcon}
              leftIconAltText="Add"
              buttonText="Add Contact Person"
              onClick={handleAdd}
            />
            <NextButton
              buttonTagCssClasses="btn-submit"
              buttonText="Next"
              onClick={handleOnClickNextButton}
              disabled={isSaving}
              loading={isSaving}
            />
          </div>
        </>
      )}

      <RegistrationVerificationModal
        open={verificationModalOpen}
        email={newEntity?.contactPersonDetails?.[0]?.email || ""}
        whatsappNumber={
          newEntity?.contactPersonDetails?.[0]?.whatsappNumber
            ? `${newEntity.contactPersonDetails[0].whatsappNumberCountryCode || "+91"}${newEntity.contactPersonDetails[0].whatsappNumber}`
            : ""
        }
        onCancel={() => setVerificationModalOpen(false)}
        onVerified={proceedToSave}
      />
    </>
  );
};

export default EntitySignupStep1;
